import { describeApiError } from '@brush/api-client';
import { ErrorRetry, Spinner } from '@brush/ui';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { importCandidates, usable } from '../../../import/candidates';
import { parseCsv } from '../../../import/csv';
import { buildPlan, fileInternalIds, type ImportPlan } from '../../../import/plan';
import {
  useHeldTournaments,
  useImportTournaments,
  usePlatformTags,
  useRoomTemplates,
  type ImportOperation,
  type ImportOutcome,
} from '../../../queries';
import { btnGhost, btnPrimary, Modal } from '../../Modal';
import { importUnfinished } from '../../../dialogDrafts';
import { useUnsavedWork } from '../../../unsaved';
import { FileStep } from './FileStep';
import { ReviewStep } from './ReviewStep';
import { RunStep } from './RunStep';
import { TemplateStep } from './TemplateStep';

type Step = 'template' | 'file' | 'review' | 'run';

const STEPS: { key: Step; label: string }[] = [
  { key: 'template', label: 'Template' },
  { key: 'file', label: 'File' },
  { key: 'review', label: 'Review' },
  { key: 'run', label: 'Import' },
];

/**
 * What the button will do, in the operator's terms. Creating and updating are different enough
 * that one word for both would hide half of it — and "nothing to import" is a real answer on a
 * re-run of a file whose schedule hasn't moved.
 */
function applyLabel(plan: ImportPlan): string {
  if (plan.pending) return 'Checking the room…';
  const created = plan.creates.length;
  const updated = plan.updates.length;

  if (created > 0 && updated > 0) return `Import ${created} new, update ${updated}`;
  if (created > 0) return `Import ${created} tournament${created === 1 ? '' : 's'}`;
  if (updated > 0) return `Apply ${updated} update${updated === 1 ? '' : 's'}`;
  return 'Nothing to import';
}

/**
 * A file this size is already far past the row cap the plan enforces; the point of the check is
 * to fail on the wrong file immediately rather than after reading a few hundred megabytes into
 * a string.
 */
const MAX_BYTES = 5 * 1024 * 1024;

function StepBar({ current }: { current: Step }) {
  const at = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="mb-6 flex shrink-0 items-center gap-2 text-xs">
      {STEPS.map((step, index) => {
        const done = index < at;
        const here = index === at;
        return (
          <li key={step.key} className="flex items-center gap-2">
            <span
              className={`flex size-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                here
                  ? 'bg-felt-700 text-white'
                  : done
                    ? 'bg-felt-50 text-felt-700'
                    : 'bg-ink-800/5 text-ink-800/40'
              }`}
            >
              {done ? <Check className="size-3" /> : index + 1}
            </span>
            <span className={here ? 'font-medium text-ink-950' : 'text-ink-800/50'}>
              {step.label}
            </span>
            {index < STEPS.length - 1 ? (
              <span className="mx-1 h-px w-4 bg-ink-800/15" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Reading a room's own file of tournaments into its schedule.
 *
 * Four steps, and the third is the point of the other three: nothing is created until an operator
 * has seen what the file says. The template decides how the file is read, so it is chosen first —
 * and only from templates this room has actually filled in, since there is no platform-level
 * content to fall back on.
 */
export function ImportTournamentsModal({
  roomId,
  defaultTags,
  onClose,
}: {
  roomId: string;
  defaultTags: string[];
  onClose: () => void;
}) {
  const templatesQ = useRoomTemplates(roomId);
  const platformTagsQ = usePlatformTags();
  const importer = useImportTournaments(roomId);

  const [step, setStep] = useState<Step>('template');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [file, setFile] = useState<{ name: string; rows: string[][] } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  // A chosen file not yet imported is asked about before a room switch discards it (#591).
  useUnsavedWork(importUnfinished({ file, outcome }));

  const candidates = useMemo(() => importCandidates(templatesQ.data ?? []), [templatesQ.data]);
  const ready = usable(candidates);

  /*
   * One template is not a choice, so it isn't presented as one — but "one" means one *candidate*,
   * not one that happens to be usable today. A room with two import templates and one still to
   * fill in has a choice to make and a reason to see the other, and skipping the picker there
   * strands them on a step with nothing to go back to.
   */
  const only = candidates.length === 1 && ready.length === 1 ? ready[0] : null;
  if (only && selectedId === null && step === 'template') {
    setSelectedId(only.entry.template.id);
    setStep('file');
  }

  const selected = candidates.find(
    (c) => c.entry.template.id === selectedId && c.fieldKeys !== null,
  );

  // The internal IDs the file names, read as the plan reads them, and the room's tournaments holding them:
  // what a re-import is compared against (known gap 108), in place of the room's capped list with every result.
  const fileIds = useMemo(
    () =>
      selected?.fieldKeys && file
        ? fileInternalIds({ fields: selected.entry.template.fields, fieldKeys: selected.fieldKeys, rows: file.rows })
        : null,
    [selected, file],
  );
  const heldQ = useHeldTournaments(roomId, fileIds);

  const plan = useMemo(() => {
    if (!selected?.fieldKeys || !file) return null;
    return buildPlan({
      roomId,
      fields: selected.entry.template.fields,
      fieldKeys: selected.fieldKeys,
      rows: file.rows,
      // What the room already holds of the file's tournaments, so a re-import can tell an unchanged
      // tournament from a changed one. Null while it is being read, which the plan waits on rather than
      // reading as an empty room (gap 112).
      existing: heldQ.data ?? null,
      platformTags: platformTagsQ.data ?? [],
    });
  }, [selected, file, roomId, heldQ.data, platformTagsQ.data]);

  const clearFile = () => {
    setFile(null);
    setFileError(null);
  };

  const takeFile = (chosen: File) => {
    setFileError(null);
    setFile(null);

    if (!/\.csv$/i.test(chosen.name)) {
      setFileError(`“${chosen.name}” isn't a CSV file. Only CSV can be imported.`);
      return;
    }
    if (chosen.size > MAX_BYTES) {
      setFileError(
        `That file is ${(chosen.size / 1024 / 1024).toFixed(1)} MB. The importer takes files up to 5 MB.`,
      );
      return;
    }

    void chosen
      .text()
      .then((text) => {
        const rows = parseCsv(text);
        if (rows.length === 0) {
          setFileError('That file is empty.');
          return;
        }
        setFile({ name: chosen.name, rows });
      })
      .catch(() => setFileError("That file couldn't be read."));
  };

  const operations: ImportOperation[] = plan
    ? [
        ...plan.creates.map((body) => ({
          kind: 'create' as const,
          internalId: body.internalId,
          body,
        })),
        ...plan.updates.map((u) => ({ kind: 'update' as const, ...u })),
      ]
    : [];

  const start = () => {
    if (operations.length === 0) return;
    setProgress(0);
    setOutcome(null);
    setStep('run');
    importer.mutate({ operations, onProgress: setProgress }, { onSuccess: setOutcome });
  };

  // ---- body ----

  let body = null;
  let footer = null;
  // What the step bar marks, which is not always `step` — see the fallback branch below.
  let shown: Step = step;

  if (templatesQ.isLoading) {
    body = <Spinner label="Loading templates…" />;
  } else if (templatesQ.isError) {
    body = (
      <ErrorRetry message="Couldn't load templates." onRetry={() => void templatesQ.refetch()} />
    );
  } else if (candidates.length === 0) {
    // Deliberately not "the platform hasn't made one": templates the room's features don't reach
    // are filtered out server-side, so the client cannot tell an empty catalogue from a gated one.
    body = (
      <p className="py-8 text-center text-sm text-ink-800/60">
        No template available to this room is set up for importing tournaments. Templates are
        defined by the platform, and can be limited to rooms running particular features.
      </p>
    );
    footer = (
      <button type="button" onClick={onClose} className={btnGhost}>
        Close
      </button>
    );
  } else if (step === 'run') {
    body = (
      <RunStep
        total={operations.length}
        done={progress}
        outcome={outcome}
        error={describeApiError(importer.error)}
      />
    );
    footer = importer.isPending ? null : (
      <button type="button" onClick={onClose} className={btnPrimary}>
        Done
      </button>
    );
  } else if (step === 'review' && plan) {
    body = !plan.pending ? (
      <ReviewStep plan={plan} defaultTags={defaultTags} />
    ) : heldQ.isError ? (
      <ErrorRetry
        message="Couldn't read this room's tournaments, so a re-import can't be told from a new one."
        onRetry={() => void heldQ.refetch()}
      />
    ) : (
      <Spinner label="Checking which of these tournaments this room already holds…" />
    );
    footer = (
      <>
        <button type="button" onClick={() => setStep('file')} className={btnGhost}>
          Back
        </button>
        <button
          type="button"
          onClick={start}
          disabled={plan.errorCount > 0 || operations.length === 0}
          className={btnPrimary}
        >
          {applyLabel(plan)}
        </button>
      </>
    );
  } else if (step === 'file' && selected?.fieldKeys) {
    body = (
      <FileStep
        templateName={selected.entry.template.name}
        fields={selected.entry.template.fields}
        fieldKeys={selected.fieldKeys}
        file={file ? { name: file.name, lines: file.rows.length } : null}
        error={fileError}
        onFile={takeFile}
        onClearFile={clearFile}
        onChangeTemplate={only ? null : () => setStep('template')}
      />
    );
    footer = (
      <>
        <button
          type="button"
          onClick={() => (only ? onClose() : setStep('template'))}
          className={btnGhost}
        >
          {only ? 'Cancel' : 'Back'}
        </button>
        <button
          type="button"
          onClick={() => setStep('review')}
          disabled={!file}
          className={btnPrimary}
        >
          Validate
        </button>
      </>
    );
  } else {
    // The template step, and the landing place for a later step whose template or file went away
    // under it — a refetch can drop a template the platform has since changed. Showing the picker
    // again is recoverable; an empty dialog is not.
    shown = 'template';
    body = (
      <TemplateStep candidates={candidates} selectedId={selectedId} onSelect={setSelectedId} />
    );
    footer = (
      <>
        <button type="button" onClick={onClose} className={btnGhost}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setStep('file')}
          disabled={!selected}
          className={btnPrimary}
        >
          Next
        </button>
      </>
    );
  }

  // Carries the two choices already made through every later step, the review included — which
  // template read the file is as much a part of what is being approved as the file itself.
  const subtitle = selected
    ? [selected.entry.template.name, file?.name].filter(Boolean).join(' · ')
    : 'Read a file of tournaments into this room’s schedule.';

  // Only the review step brings its own scrolling region; the other three are ordinary content
  // that should scroll the dialog in the usual way.
  const fills = shown === 'review';

  return (
    <Modal
      title="Import tournaments"
      subtitle={subtitle}
      onClose={onClose}
      width="max-w-4xl"
      fill={fills}
      footer={footer}
    >
      {candidates.length > 0 ? <StepBar current={shown} /> : null}
      {fills ? <div className="flex min-h-0 flex-1 flex-col">{body}</div> : body}
    </Modal>
  );
}
